import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  MessageSquare,
  Calendar,
  Users,
  Wrench,
  AlertTriangle,
  CheckCircle,
  Clock,
  TrendingUp,
  Building2,
  Phone,
  Mail
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

interface DashboardStats {
  totalToday: number;
  total7Days: number;
  total30Days: number;
  byType: Record<string, number>;
  byCamping: Record<string, number>;
  urgent: number;
  pending: number;
  recentActivity: any[];
}

const AdminDashboard = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalToday: 0,
    total7Days: 0,
    total30Days: 0,
    byType: {},
    byCamping: {},
    urgent: 0,
    pending: 0,
    recentActivity: []
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardStats();
  }, []);

  const loadDashboardStats = async () => {
    try {
      setLoading(true);
      
      // Charger toutes les soumissions
      const { data: submissions, error } = await supabase
        .from('admin_submissions_with_history')
        .select('*')
        .order('submitted_at', { ascending: false });
      
      if (error) {
        console.error('Erreur chargement soumissions:', error);
        return;
      }
      
      const submissionsData = submissions || [];
      
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const sevenDaysAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
      const thirtyDaysAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);

      // Calculer les statistiques
      const totalToday = submissionsData.filter(s =>
        new Date(s.submitted_at) >= today
      ).length;

      const total7Days = submissionsData.filter(s => 
        new Date(s.submitted_at) >= sevenDaysAgo
      ).length;

      const total30Days = submissionsData.filter(s => 
        new Date(s.submitted_at) >= thirtyDaysAgo
      ).length;

      // Par type
      const byType: Record<string, number> = {};
      submissionsData.forEach(s => {
        const type = s.form_type || 'other';
        byType[type] = (byType[type] || 0) + 1;
      });

      // Par camping
      const byCamping: Record<string, number> = {};
      submissionsData.forEach(s => {
        if (s.camping_site || s.form_data?.camping || s.form_data?.site) {
          const camping = s.camping_site || s.form_data?.camping || s.form_data?.site;
          byCamping[camping] = (byCamping[camping] || 0) + 1;
        }
      });

      // Urgent et en attente
      const urgent = submissionsData.filter(s => s.status === 'urgent').length;
      const pending = submissionsData.filter(s => s.status === 'new' || s.status === 'urgent').length;

      // Activité récente (dernières 10)
      const recentActivity = submissionsData
        .sort((a, b) => new Date(b.submitted_at).getTime() - new Date(a.submitted_at).getTime())
        .slice(0, 10);

      setStats({
        totalToday,
        total7Days,
        total30Days,
        byType,
        byCamping,
        urgent,
        pending,
        recentActivity
      });

    } catch (error) {
      console.error('Erreur lors du chargement des statistiques:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      technical: 'Technique',
      leave: 'Congés',
      professional: 'Professionnel',
      delivery: 'Livraison',
      seasonal: 'Saisonnier',
      group: 'Groupe',
      bike: 'Vélo',
      reservation: 'Réservation',
      contact: 'Contact',
      partner: 'Partenaire',
      other: 'Autre'
    };
    return labels[type] || type;
  };

  const getTypeIcon = (type: string) => {
    const icons: Record<string, any> = {
      technical: Wrench,
      leave: Calendar,
      professional: Building2,
      delivery: Phone,
      seasonal: Users,
      group: Users,
      bike: Wrench,
      reservation: Calendar,
      contact: Mail,
      partner: Building2,
      other: MessageSquare
    };
    const Icon = icons[type] || MessageSquare;
    return <Icon className="h-4 w-4" />;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'urgent': return 'bg-red-500';
      case 'in_progress': return 'bg-blue-500';
      case 'completed': return 'bg-green-500';
      default: return 'bg-gray-500';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6 min-h-0">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-6">
                <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 rounded w-1/2"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 min-h-0">
      {/* Métriques principales */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Aujourd'hui</p>
                <p className="text-3xl font-bold text-gray-900">{stats.totalToday}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <MessageSquare className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-green-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">7 derniers jours</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total7Days}</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <TrendingUp className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-red-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Urgent</p>
                <p className="text-3xl font-bold text-gray-900">{stats.urgent}</p>
              </div>
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-yellow-500">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">En attente</p>
                <p className="text-3xl font-bold text-gray-900">{stats.pending}</p>
              </div>
              <div className="p-3 bg-yellow-100 rounded-full">
                <Clock className="h-6 w-6 text-yellow-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Demandes par type */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <MessageSquare className="h-5 w-5" />
              Demandes par type
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats.byType)
                .sort(([,a], [,b]) => b - a)
                .slice(0, 8)
                .map(([type, count]) => (
                <div key={type} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    {getTypeIcon(type)}
                    <span className="font-medium">{getTypeLabel(type)}</span>
                  </div>
                  <Badge variant="secondary" className="bg-primary/10 text-primary">
                    {count}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Demandes par camping */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Demandes par camping
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(stats.byCamping)
                .sort(([,a], [,b]) => b - a)
                .slice(0, 8)
                .map(([camping, count]) => (
                <div key={camping} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <Building2 className="h-4 w-4 text-gray-500" />
                    <span className="font-medium text-sm">{camping}</span>
                  </div>
                  <Badge variant="secondary" className="bg-primary/10 text-primary">
                    {count}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Activité récente */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Activité récente
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {stats.recentActivity.map((activity, index) => (
              <div key={activity.id} className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className={`w-3 h-3 rounded-full ${getStatusColor(activity.status)}`}></div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {getTypeIcon(activity.form_type)}
                    <span className="font-medium text-sm">{getTypeLabel(activity.form_type)}</span>
                    <Badge variant="outline" className="text-xs">
                      {activity.user_name || activity.form_data?.name || activity.form_data?.nom || 'Anonyme'}
                    </Badge>
                  </div>
                  <p className="text-sm text-gray-600 truncate">
                    {activity.message || activity.form_data?.message || activity.form_data?.description || 'Pas de message'}
                  </p>
                </div>
                <div className="text-xs text-gray-500">
                  {new Date(activity.submitted_at).toLocaleDateString('fr-FR', {
                    day: '2-digit',
                    month: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminDashboard;